import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { payAndMakeEventBooking } from 'src/consumer/components/EventBooking/BookingHelperFunctions'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { PublicAppFooter } from 'src/consumer/components/PublicAppFooter'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import { ClubbedAuthModal } from 'src/consumer/components/SignIn/ClubbedAuthComponents'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, platformWeb, getCountryCode } from 'src/consumer/services'
import {
  getDataWithoutParams,
  getPublicDataWithoutParams,
} from 'src/consumer/services/featureServices'
import { platformOptions } from '../../../consumer/constants'
import { CPBPOMBookAndPayButton, CPBPOMContainer } from './ClinicBrandPageStyles'
import { Spinner } from 'react-bootstrap'
import { loadStripe } from '@stripe/stripe-js'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import confirmationTickLottie from 'src/consumer/lotties/confirmationTickLottie.json'
import { TickLottie } from 'src/consumer/components/EventBooking/EventBookingStyles'
import { getImageLink, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

export default function ClinicPublicBookingPageOfMentor() {
  const navigate = useNavigate()
  const location = useLocation()
  const { clinicBrandSlug, clinicId, mentorSlug, eventId, clinicSlug } = useParams()
  const { profileData } = useContext(ProfileContext)
  const [mentorData, setMentorData] = useState()
  const [dataLoading, setDataLoading] = useState(false)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [typeOfClubbedAuthModal, setTypeOfClubbedAuthModal] = useState('signup')
  const [eventDetailOfRequestedEvent, setEventDetailOfRequestedEvent] = useState()
  const [stripePromise, setStripePromise] = useState(null)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [pageState, setPageState] = useState('booking')
  const [clinicBrandData, setClinicBrandData] = useState()

  const [availableTimeSlotsArray, setAvailableTimeSlotsArray] = useState([])
  const [nextSlotsLoading, setNextSlotsLoading] = useState(false)
  const [subscriptionPlanData, setSubscriptionPlanData] = useState()

  const [initiatedBookingData, setInitiatedBookingData] = useState()
  const [bookedEventData, setBookedEventData] = useState()

  useEffect(() => {
    let cancelled = false
    async function run() {
      const brandResponse = await getDataWithoutParams(
        `/api/external/clinic/brand-and-clinics?slug=${clinicBrandSlug}`,
      )
      if (cancelled) return
      if (brandResponse?.status !== 200) {
        navigate('/404')
        return
      }
      const brandPayload = brandResponse?.data?.data ?? brandResponse?.data
      const brandCode = brandResponse?.data?.code
      if (!brandPayload || (brandCode !== undefined && !isCommonResponseSuccessful(brandCode))) {
        navigate('/404')
        return
      }
      setClinicBrandData(brandPayload)
      const thisClinic = brandPayload?.clinics?.find((c) => c?.id === clinicId || c?.slug === clinicSlug)
      const allPractitioners = [
        ...(thisClinic?.doctors || []),
        ...(thisClinic?.therapists || []),
      ]
      const mentorRef = allPractitioners.find((p) => p?.slug === mentorSlug) || allPractitioners.find((p) => p?.uid)
      const mentorId = mentorRef?.uid || mentorRef?.id
      const resolvedClinicId = thisClinic?.id ?? clinicId
      if (mentorId && eventId) {
        const from = dayjs().startOf('month').format('YYYY-MM-DD')
        const to = dayjs().endOf('month').format('YYYY-MM-DD')
        const timeZone = encodeURIComponent(getTimezoneOffset())
        let query = `mentorId=${encodeURIComponent(mentorId)}&eventId=${encodeURIComponent(eventId)}&from=${from}&to=${to}&timeZone=${timeZone}`
        if (resolvedClinicId) {
          query += `&clinicId=${encodeURIComponent(resolvedClinicId)}`
        }
        let response = await getPublicDataWithoutParams(
          `/api/public/clinic-booking-page?${query}`,
        )
        if (cancelled) return
        if (response?.response) {
          response = { status: response.response.status, data: response.response.data }
        }
        const status = response?.status
        if (status === 404 || status === 400) {
          navigate('/404')
          setDataLoading(false)
          return
        }
        if (status === 200) {
          const data = response?.data?.data ?? response?.data
          const bookingPage = data?.bookingPage
          if (bookingPage?.mentor && (bookingPage.mentor.id || bookingPage.mentor.slug)) {
            setEventDetailOfRequestedEvent(bookingPage.event ?? null)
            const slots =
              Array.isArray(bookingPage.availableSlots) &&
              bookingPage.availableSlots.map(({ date, slots: s }) => ({
                date,
                availableTimeSlots: Array.isArray(s) ? s : [],
              }))
            setAvailableTimeSlotsArray(slots ?? [])
            const mentorRef = bookingPage.mentor
            const profileResp = await getPublicDataWithoutParams(
              `/api/service-provider?id=${encodeURIComponent(mentorRef.id)}&slug=${encodeURIComponent(mentorRef.slug || '')}`,
            )
            if (cancelled) return
            const profile = profileResp?.status === 200 ? profileResp?.data : null
            if (profile && typeof profile === 'object') {
              setMentorData({ ...mentorRef, ...profile })
            } else {
              setMentorData(mentorRef)
            }
            setDataLoading(false)
            return
          }
        }
      }
      navigate('/404')
      setDataLoading(false)
    }
    setDataLoading(true)
    run()
    return () => { cancelled = true }
  }, [clinicBrandSlug, clinicId, clinicSlug, mentorSlug, eventId, navigate])

  useEffect(() => {
    if (!mentorData?.id || !eventId || !clinicBrandData) return
    const thisClinic = clinicId
      ? clinicBrandData?.clinics?.find((c) => c?.id === clinicId)
      : clinicBrandData?.clinics?.find((c) => c?.slug === clinicSlug)
    const resolvedClinicId = thisClinic?.id ?? clinicId
    async function refreshSlots() {
      setNextSlotsLoading(true)
      const from = dayjs().startOf('month').format('YYYY-MM-DD')
      const to = dayjs().endOf('month').format('YYYY-MM-DD')
      const timeZone = encodeURIComponent(getTimezoneOffset())
      let query = `mentorId=${encodeURIComponent(mentorData.id)}&eventId=${encodeURIComponent(eventId)}&from=${from}&to=${to}&timeZone=${timeZone}`
      if (resolvedClinicId) query += `&clinicId=${encodeURIComponent(resolvedClinicId)}`
      let response = await getPublicDataWithoutParams(
        `/api/public/clinic-booking-page?${query}`,
      )
      if (response?.response) {
        response = { status: response.response.status, data: response.response.data }
      }
      if (response?.status === 200) {
        const data = response?.data?.data ?? response?.data
        const bookingPage = data?.bookingPage
        if (Array.isArray(bookingPage?.availableSlots)) {
          const slots = bookingPage.availableSlots.map(({ date, slots: s }) => ({
            date,
            availableTimeSlots: Array.isArray(s) ? s : [],
          }))
          setAvailableTimeSlotsArray(slots)
        }
      }
      setNextSlotsLoading(false)
    }
    refreshSlots()
    const intervalId = setInterval(refreshSlots, 20000)
    return () => clearInterval(intervalId)
  }, [mentorData, eventId, clinicBrandData, clinicId, clinicSlug])

  useEffect(() => {
    loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY).then((result) => {
      setStripePromise(result)
    })
  }, []) // making stripe promise

  async function getAndSetBookedEventData() {
    if (initiatedBookingData?.bookedEvent?.id) {
      const bookedEventDetailResponse = await getDataWithoutParams(
        `/api/secure/user/booked-slot/details?eventId=${initiatedBookingData?.bookedEvent?.id}`,
      )
      if (
        bookedEventDetailResponse?.status === 200 &&
        isCommonResponseSuccessful(bookedEventDetailResponse?.data?.code)
      ) {
        setBookedEventData(bookedEventDetailResponse?.data?.data?.bookedEvent)
      }
    }
  }

  async function handleBookButtonClick() {
    if (isTokenInvalidOrAbsent(localStorage.getItem('token'))) {
      setShowClubbedAuthModal(true)
    } else {
      setPaymentLoading(true)
      await payAndMakeEventBooking(
        eventDetailOfRequestedEvent, // bookingEventDetails
        {
          data: {
            startTime: availableTimeSlotsArray?.find(
              (item) => item?.date === dayjs()?.format('YYYY-MM-DD'),
            )?.availableTimeSlots?.[0]?.startTime,
            endTime: availableTimeSlotsArray?.find(
              (item) => item?.date === dayjs()?.format('YYYY-MM-DD'),
            )?.availableTimeSlots?.[0]?.endTime,
          },
          postBufferMins: 0,
          preBufferMins: 0,
        }, // chosenSlotData
        'razorpay', // paymentGateway
        'GFT-default', // giftCardId
        stripePromise, // stripePromise
        mentorData?.id, // mentorId
        profileData, // userProfileData
        () => null, // giftCodeErrorSetter
        () => {
          setPageState('confirmed')
        }, // afterConfirmationFunction
        'uid',
        'bookNextAvailable',
        (data) => setInitiatedBookingData(data),
      )
      setPaymentLoading(false)
    }
  }

  async function getAndSetSubscriptionPlanData() {
    if (eventDetailOfRequestedEvent?.subscriptionPlanId) {
      const response = await getDataWithoutParams(
        `/api/subscription-plan?id=${
          eventDetailOfRequestedEvent?.subscriptionPlanId
        }&zoneOffset=${encodeURIComponent(
          getTimezoneOffset(),
        )}&appVersion=${appVersion}&platformType=${platformWeb}&languageCode=${sessionStorage.getItem(
          'languageCode',
        )}&platform=${platformWeb}&countryCode=${getCountryCode()}`,
      )
      if (response?.status === 200 && response?.data) {
        setSubscriptionPlanData({ ...response?.data })
      }
    }
  }

  useEffect(() => {
    getAndSetSubscriptionPlanData()
  }, [eventDetailOfRequestedEvent])

  let thisClinicData
  if (clinicId) {
    thisClinicData = clinicBrandData?.clinics?.find((item) => item?.id === clinicId)
  } else if (clinicSlug) {
    thisClinicData = clinicBrandData?.clinics?.find((item) => item?.slug === clinicSlug)
  }

  async function whenPageChanges() {
    if (pageState === 'confirmed' && !Boolean(bookedEventData)) {
      await getAndSetBookedEventData()
    }
  }

  useEffect(() => {
    whenPageChanges()
  }, [pageState])

  return (
    <>
      {dataLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <>
          <PublicAppHeader
            loginFunction={() => {
              setTypeOfClubbedAuthModal('login')
              setShowClubbedAuthModal(true)
            }}
            registerFunction={() => {
              setTypeOfClubbedAuthModal('signup')
              setShowClubbedAuthModal(true)
            }}
            config={{ showLinks: false }}
          />
          <ClubbedAuthModal
            open={showClubbedAuthModal}
            onClose={() => {
              setShowClubbedAuthModal(false)
            }}
            loginVariant={typeOfClubbedAuthModal === 'login'}
            nameFieldHeading="Patient Name"
          />
          {pageState === 'booking' && (
            <CPBPOMContainer>
              <div>{thisClinicData?.title}</div>
              <div>{eventDetailOfRequestedEvent?.title}</div>
              {platformOptions?.find(
                (item) => item?.id === eventDetailOfRequestedEvent?.meetingSrc,
              )?.name && (
                <div>
                  (
                  {
                    platformOptions.find(
                      (item) => item?.id === eventDetailOfRequestedEvent?.meetingSrc,
                    )?.name
                  }
                  )
                </div>
              )}
              <div style={{ marginTop: '10px' }}>Book a session with</div>
              <div>
                <b>{mentorData?.firstName}</b>
              </div>
              {mentorData?.profilePicId && (
                <img
                  style={{ width: '150px', borderRadius: '16px', margin: '5px 0 5px 0' }}
                  src={getImageLink(mentorData?.profilePicId)}
                  alt="mentor"
                />
              )}
              <div style={{ fontWeight: '500', fontSize: '20px', marginTop: '30px' }}>
                {dayjs()?.format('DD-MMM-YYYY')}
              </div>
              <div style={{ fontSize: '14px' }}>Today</div>
              <div style={{ marginTop: '10px' }}>
                {'Next Available Slot '}
                {nextSlotsLoading && <Spinner animation="border" variant="dark" size="sm" />}
              </div>
              {availableTimeSlotsArray?.find(
                (item) => item?.date === dayjs()?.format('YYYY-MM-DD'),
              )?.availableTimeSlots?.length > 0 ? (
                <>
                  <div>
                    <b>
                      {dayjs(
                        availableTimeSlotsArray?.find(
                          (item) => item?.date === dayjs()?.format('YYYY-MM-DD'),
                        )?.availableTimeSlots?.[0]?.startTime,
                      )?.format('h:mm A')}{' '}
                    </b>
                  </div>
                  <CPBPOMBookAndPayButton
                    style={{ marginTop: '10px' }}
                    onClick={handleBookButtonClick}
                    disabled={paymentLoading}
                  >
                    {'Pay and Book'}
                    {paymentLoading && <Spinner animation="border" variant="light" size="sm" />}
                  </CPBPOMBookAndPayButton>
                </>
              ) : (
                <div>
                  {nextSlotsLoading ? (
                    <Spinner animation="border" variant="dark" size="sm" />
                  ) : (
                    <div style={{ fontSize: '10px' }}>No Slots Available</div>
                  )}
                </div>
              )}
              <div
                style={{
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'center',
                  marginBottom: '20px',
                  marginTop: '20px',
                  fontSize: '12px',
                }}
                onClick={() => navigate(`/mentor/${mentorData?.slug}/events/${eventId}`)}
              >
                <u>Click here to book future dates</u>
              </div>
              <div
                style={{
                  width: '100%',
                  textAlign: 'center',
                  marginBottom: '20px',
                  marginTop: '20px',
                }}
              >
                {subscriptionPlanData?.discountedPrice && subscriptionPlanData?.introductoryPrice && (
                  <>
                    <div>
                      Old Patient Fee:{' '}
                      <span>
                        {subscriptionPlanData?.currencySymbol}
                        {subscriptionPlanData?.discountedPrice}
                      </span>
                    </div>
                    <div>
                      New Patient Fee (First Visit):{' '}
                      <span>
                        {subscriptionPlanData?.currencySymbol}
                        {subscriptionPlanData?.introductoryPrice}
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div
                style={{
                  marginTop: '20px',
                  marginBottom: '20px',
                  fontSize: '12px',
                  textAlign: 'center',
                  width: '300px',
                }}
              >
                Note: Appointments might be delayed due to existing appointments or emergencies.
              </div>
            </CPBPOMContainer>
          )}
          {pageState === 'confirmed' && (
            <CPBPOMContainer>
              <TickLottie animationData={confirmationTickLottie} loop={false} autoPlay={true} />
              <div>
                <b>Session Confirmed</b>
              </div>
              {bookedEventData?.appointmentNumber && (
                <>
                  <div style={{ marginTop: '30px' }}>Your Appointment Number is</div>
                  <div style={{ fontSize: '50px' }}>
                    <b>{bookedEventData?.appointmentNumber}</b>
                  </div>
                </>
              )}
              <div onClick={() => navigate('/my-live-sessions')} style={{ marginTop: '40px' }}>
                <u>View your booked sessions</u>
              </div>
              <div
                style={{ marginTop: '20px' }}
                onClick={() => {
                  // Explicit wellness opt-in so logged-in workforce
                  // users (e.g. a doctor previewing their own public
                  // booking page) don't get bounced to their portal
                  // when they click "Explore AUMHUM App".
                  try {
                    sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
                    sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
                    sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
                  } catch { /* private-browsing: handled downstream */ }
                  navigate('/wellness')
                }}
              >
                <u>Explore AUMHUM App</u>
              </div>
            </CPBPOMContainer>
          )}
        </>
      )}
    </>
  )
}
